import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const appLogo = new URL('../assets/bbyb-logo.png', import.meta.url);

// Cozy campfire palette
const C = {
    primary:   '#895129',
    gold:      '#AD7A32',
    olive:     '#8A6E29',
    dark:      '#660F09',
};

const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
    </svg>
);

const EyeHideIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
);

const quotes = [
    { label: 'Secure',      text: 'Your files, your cloud — no subscriptions, no third parties.' },
    { label: 'Flexible',    text: 'Store on B2, MinIO, or local disk. Switch anytime.' },
    { label: 'Smart',       text: 'Files untouched for 90+ days are auto-archived to save space.' },
];

const QuoteFader = () => {
    const [current, setCurrent] = useState(0);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const timer = setInterval(() => {
            setVisible(false);
            setTimeout(() => {
                setCurrent(i => (i + 1) % quotes.length);
                setVisible(true);
            }, 400);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const q = quotes[current];
    return (
        <div className="mt-5 text-center max-w-sm px-4" style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s' }}>
            <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mr-2 text-white" style={{ backgroundColor: C.gold }}>
                {q.label}
            </span>
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{q.text}</span>
        </div>
    );
};

const Login = ({ onLogin }) => {
    const [isRegister, setIsRegister] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const savedUsername = localStorage.getItem('lastUsername');
    const savedEmail = localStorage.getItem('lastEmail');
    const isReturningUser = !!(savedUsername && savedEmail);
    const [switchingUser, setSwitchingUser] = useState(false);

    const showEmailField = !isReturningUser || switchingUser || isRegister;

    useEffect(() => {
        if (isReturningUser && !switchingUser && !isRegister) {
            setEmail(savedEmail);
        }
    }, [isReturningUser, switchingUser, isRegister, savedEmail]);

    const [inviteToken, setInviteToken] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteExpiry, setInviteExpiry] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('invite');
        if (!token) return;
        setInviteToken(token);
        api.validateInvite(token)
            .then(data => {
                if (data.valid) {
                    setIsRegister(true);
                    setEmail(data.email || '');
                    setInviteEmail(data.email || '');
                    setInviteExpiry(data.expires_at);
                } else {
                    setError('This invitation link is invalid or has already been used.');
                }
            })
            .catch(() => {
                setError('This invitation link is invalid or has already been used.');
            });
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await api.login(email, password);
            localStorage.setItem('lastEmail', email);
            onLogin(data.token, data.refresh_token, data.user);
        } catch (err) {
            if (err.message === 'account_pending_deletion') {
                setError('This account is scheduled for deletion. Contact an admin to restore it.');
            } else {
                setError('Invalid credentials.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await api.registerWithInvite({ username: name, email, password, invite_token: inviteToken });
            const data = await api.login(email, password);
            localStorage.setItem('lastEmail', email);
            onLogin(data.token, data.refresh_token, data.user);
        } catch (err) {
            const msg = err.message || '';
            if (msg === 'username_taken') {
                setError('username_taken');
            } else if (msg === 'email_taken') {
                setError('email_taken');
            } else {
                setError(msg || 'Registration failed.');
            }
        } finally {
            setLoading(false);
        }
    };

    const switchToLogin = () => {
        setIsRegister(false);
        setError('');
        setName('');
        setEmail('');
        setPassword('');
    };

    const handleSwitchUser = () => {
        setSwitchingUser(true);
        setEmail('');
        setPassword('');
        setError('');
    };

    const handleBackToSaved = () => {
        setSwitchingUser(false);
        setEmail(savedEmail);
        setPassword('');
        setError('');
    };

    const getHeading = () => {
        if (isRegister) return 'Create an account,';
        if (isReturningUser && !switchingUser) return 'Welcome back,';
        return 'Sign in to';
    };

    const getSubheading = () => {
        if (isRegister) return '';
        if (isReturningUser && !switchingUser) return savedUsername;
        return 'BrickByBrickVault';
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10" style={{ backgroundColor: '#FFE1CC' }}>
            {/* Card */}
            <div className="w-full max-w-sm rounded-2xl sm:rounded-3xl px-5 py-7 sm:px-8 sm:py-10 shadow-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.96)' }}>
                {/* Logo + app name */}
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                    <img src={appLogo.href} alt="BrickByBrickVault logo" className="h-7 w-auto object-contain rounded" />
                    <span className="text-sm font-bold tracking-widest text-gray-400">BrickByBrickVault</span>
                </div>

                {/* Heading */}
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 leading-tight">
                    {getHeading()}
                </h1>
                {getSubheading() && (
                    <p className="text-sm sm:text-base font-bold italic text-gray-600 opacity-80 mt-1 mb-5 sm:mb-7">{getSubheading()}</p>
                )}
                {!getSubheading() && <div className="mb-5 sm:mb-7" />}

                {error && (
                    <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
                        {error === 'username_taken' ? (
                            <>That username is already taken. Try a different one.</>
                        ) : error === 'email_taken' ? (
                            <>An account with this email already exists.{' '}
                            <button type="button" onClick={switchToLogin} className="underline font-medium hover:text-red-800">Log in instead?</button></>
                        ) : error}
                    </div>
                )}

                {isRegister && inviteToken && inviteExpiry && (
                    <div className="mb-4 px-3 py-2 text-sm rounded-lg" style={{ backgroundColor: '#fdf3e7', border: `1px solid ${C.gold}`, color: C.dark }}>
                        You've been invited — link expires on {new Date(inviteExpiry).toLocaleDateString('en-GB')}.
                    </div>
                )}

                <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-3 sm:space-y-4">
                    {isRegister && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-gray-700 text-sm font-medium">Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                className="w-full px-4 py-3 bg-white rounded-lg text-base focus:outline-none text-gray-800 placeholder-gray-300 border border-gray-100"
                                style={{ '--tw-ring-color': C.gold }}
                                onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${C.gold}40`}
                                onBlur={e => e.target.style.boxShadow = ''}
                                placeholder="Your name"
                                required
                            />
                        </div>
                    )}

                    {showEmailField && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-gray-700 text-sm font-medium">{isRegister ? 'Email' : 'Email or username'}</label>
                            <input
                                type={isRegister ? 'email' : 'text'}
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                readOnly={!!(inviteToken && inviteEmail)}
                                className={`w-full px-4 py-3 bg-white rounded-lg text-base focus:outline-none text-gray-800 placeholder-gray-300 border border-gray-100${(inviteToken && inviteEmail) ? ' bg-gray-50 cursor-not-allowed opacity-75' : ''}`}
                                onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${C.gold}40`}
                                onBlur={e => e.target.style.boxShadow = ''}
                                placeholder={isRegister ? 'username@gmail.com' : 'Email or username'}
                                required
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <label className="text-gray-700 text-sm font-medium">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className="w-full px-4 py-3 pr-12 bg-white rounded-lg text-base focus:outline-none text-gray-800 placeholder-gray-300 border border-gray-100"
                                onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${C.gold}40`}
                                onBlur={e => e.target.style.boxShadow = ''}
                                placeholder="Password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(v => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? <EyeIcon /> : <EyeHideIcon />}
                            </button>
                        </div>
                    </div>

                    {!isRegister && (
                        <div className="text-right">
                            <button type="button" className="text-sm font-medium tracking-wide" style={{ color: C.gold }}>
                                Forgot Password?
                            </button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 text-white text-sm font-bold rounded-lg transition disabled:opacity-60 hover:opacity-90"
                        style={{ backgroundColor: C.primary }}
                    >
                        {loading
                            ? (isRegister ? 'Creating account…' : 'Signing in…')
                            : (isRegister ? 'Create an account' : 'Sign in')}
                    </button>
                </form>

                <p className="text-center text-gray-400 text-xs mt-4 sm:mt-5 mb-3">Or continue with</p>
                <div className="flex gap-3 justify-center">
                    {[
                        <svg className="w-5 h-5" viewBox="0 0 24 24" key="g"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>,
                        <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24" key="fb"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
                    ].map((icon, i) => (
                        <button key={i} disabled className="flex items-center justify-center w-12 h-10 bg-white rounded-full shadow-sm opacity-40 cursor-not-allowed">
                            {icon}
                        </button>
                    ))}
                </div>

                {!isRegister && isReturningUser && !switchingUser && (
                    <p className="text-center text-sm text-gray-400 mt-4 sm:mt-5">
                        Not {savedUsername}?{' '}
                        <button onClick={handleSwitchUser} className="font-semibold hover:underline" style={{ color: C.primary }}>
                            Sign in with another account
                        </button>
                    </p>
                )}

                {!isRegister && switchingUser && savedUsername && (
                    <p className="text-center text-sm text-gray-400 mt-4 sm:mt-5">
                        <button onClick={handleBackToSaved} className="font-semibold hover:underline" style={{ color: C.primary }}>
                            Sign in as {savedUsername}
                        </button>
                    </p>
                )}

                {!isRegister && !isReturningUser && !switchingUser && (
                    <p className="text-center text-sm text-gray-500 mt-5">
                        Access is by invitation only.
                    </p>
                )}

                {isRegister && (
                    <p className="text-center text-sm text-gray-500 mt-5">
                        Already have an account? <button onClick={switchToLogin} className="font-bold text-gray-700 hover:underline">Sign in</button>
                    </p>
                )}
            </div>

            {/* Compact fading tagline below card */}
            <QuoteFader />
        </div>
    );
};

export default Login;
